import { useCallback } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog"
import { TextArea } from "@/components/ui/input"
import {
  CallCaseSummaryFeedbackSchema,
  CallCaseSummaryFeedbackSchemaType,
  FEEDBACK_TEXT_MAX,
} from "@/modules/patients/detail/progress-note/components/call-case-summary-feedback/schema/call-case-summary-feedback-schema"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (feedbackText: string) => void
  isSubmitting: boolean
}

const CallCaseSummaryFeedbackDialog = ({ open, onOpenChange, onSubmit, isSubmitting }: Props) => {
  const { t } = useTranslation()

  const form = useForm<CallCaseSummaryFeedbackSchemaType>({
    mode: "onChange",
    resolver: zodResolver(CallCaseSummaryFeedbackSchema),
    defaultValues: { feedbackText: "" },
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        reset({ feedbackText: "" })
      }
      onOpenChange(next)
    },
    [onOpenChange, reset],
  )

  const handleFormSubmit = useCallback(
    (data: CallCaseSummaryFeedbackSchemaType) => {
      onSubmit(data.feedbackText.trim())
    },
    [onSubmit],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent variant="small" data-testid="call-case-summary-feedback-dialog">
            <DialogHeader className="flex flex-row items-start justify-between px-6 pt-6 pb-2">
              <span
                className="text-on-surface text-xl leading-7 font-bold"
                data-testid="call-case-summary-feedback-dialog-title">
                {t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.title")}
              </span>
              <DialogClose className="static!" />
            </DialogHeader>

            <div className="flex flex-col gap-4 px-6 py-4">
              <Controller
                name="feedbackText"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    label={t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.placeholder")}
                    textLimit={FEEDBACK_TEXT_MAX}
                    error={errors.feedbackText?.message}
                    translateError
                    data-testid="call-case-summary-feedback-textarea"
                  />
                )}
              />
            </div>

            <DialogFooter className="flex justify-end gap-2 p-6 pt-4">
              <Button
                variant="secondary"
                onClick={() => handleOpenChange(false)}
                data-testid="call-case-summary-feedback-cancel-button">
                {t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.cancel")}
              </Button>
              <Button
                onClick={handleSubmit(handleFormSubmit)}
                isLoading={isSubmitting}
                data-testid="call-case-summary-feedback-submit-button">
                {t("episodeOfCare.progressNote.anamnesis.callCaseSummaryFeedback.submit")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </FormProvider>
    </Dialog>
  )
}

export { CallCaseSummaryFeedbackDialog }
